import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, Clock, ChevronsRight } from "lucide-react";
import { CoverImage } from "@/components/CoverImage";
import { readingTime } from "@/lib/reading";
import { prefetchArticle } from "@/lib/prefetch";

const AUTOPLAY_MS = 7000;

export function HeroCarousel({ stories = [] }) {
  const items = stories.slice(0, 5);
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const touchX = useRef(null);

  const n = items.length;
  const go = useCallback((next) => {
    setDir(next > index || (index === n - 1 && next === 0) ? 1 : -1);
    setIndex(((next % n) + n) % n);
  }, [index, n]);
  const advance = useCallback(() => { setDir(1); setIndex((i) => (i + 1) % n); }, [n]);

  // Autoplay + restart progress bar on index change
  useEffect(() => {
    if (n <= 1 || paused) return;
    const t = setTimeout(advance, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, paused, advance, n]);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    try { localStorage.setItem("heroSwipeHintSeen", "1"); } catch (_) {}
  }, []);

  // Pista de swipe la primera vez en móvil
  useEffect(() => {
    if (typeof window === "undefined" || window.innerWidth >= 1024 || n <= 1) return;
    try { if (localStorage.getItem("heroSwipeHintSeen")) return; } catch (_) {}
    setShowHint(true);
    const t = setTimeout(() => dismissHint(), 4500);
    return () => clearTimeout(t);
  }, [n, dismissHint]);

  if (!n) return null;
  const active = items[index];
  const next = items[(index + 1) % n];

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; if (showHint) dismissHint(); };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? index + 1 : index - 1);
    touchX.current = null;
  };

  const variants = {
    enter: (d) => ({ opacity: 0, scale: 1.04, x: d > 0 ? 60 : -60 }),
    center: { opacity: 1, scale: 1, x: 0 },
    exit: (d) => ({ opacity: 0, scale: 1.02, x: d > 0 ? -60 : 60 }),
  };

  return (
    <section className="relative section-pad pt-6 pb-10 overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1400px]">
        <div
          className="hero-stage relative rounded-3xl text-[#FAF9F6] overflow-hidden border border-white/10 p-4 sm:p-6 lg:p-8"
          data-testid="home-hero-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* decorative glows */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-28 -left-24 w-[460px] h-[460px] rounded-full bg-[#2A00D9]/40 blur-[130px]" />
            <div className="absolute -bottom-28 right-[-60px] w-[380px] h-[380px] rounded-full bg-[#B4D844]/18 blur-[120px]" />
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-7 items-stretch">
            {/* ACTIVE SLIDE */}
            <div className="lg:col-span-8 xl:col-span-9 relative rounded-2xl overflow-hidden bg-[#141414] min-h-[440px] sm:min-h-[520px] lg:min-h-[560px] group touch-pan-y" data-testid="hero-active-slide" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <AnimatePresence initial={false} custom={dir} mode="wait">
                <motion.div
                  key={active.id || index}
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.55, ease: [0.22, 0.65, 0.23, 1] }}
                  className="absolute inset-0"
                >
                  <Link
                    to={`/noticias/${active.slug}`}
                    onMouseEnter={() => prefetchArticle(active.slug)}
                    className="block absolute inset-0"
                    data-testid="hero-slide-cta-link"
                  >
                    <CoverImage src={active.cover_image} alt={active.cover_image_alt || active.title} ratio="16/10" priority className="absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/40 to-transparent" />

                    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9 lg:p-11">
                      <div className="flex flex-wrap items-center gap-2.5 mb-4">
                        <span className="inline-flex items-center rounded-full bg-[#B4D844] text-[#0E0E0E] text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-1.5" data-testid="hero-slide-tag">
                          {active.format || "Portada"}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70">
                          <Clock size={13} /> {readingTime(active.dek, active.body)} min
                        </span>
                      </div>
                      <motion.h2
                        key={`t-${active.id || index}`}
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, delay: 0.12 }}
                        className="display font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl lg:text-5xl xl:text-6xl max-w-4xl"
                        style={{ color: "#FFFFFF" }}
                        data-testid="hero-slide-title"
                      >
                        {active.title}
                      </motion.h2>
                      {active.dek && (
                        <motion.p
                          key={`d-${active.id || index}`}
                          initial={{ opacity: 0, y: 18 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.45, delay: 0.2 }}
                          className="mt-4 text-sm sm:text-base lg:text-lg text-white/80 max-w-2xl line-clamp-2"
                          data-testid="hero-slide-dek"
                        >
                          {active.dek}
                        </motion.p>
                      )}
                      <div className="mt-5 flex items-center gap-4">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#0E0E0E] font-bold text-sm px-5 py-2.5 transition-transform group-hover:-translate-y-0.5">
                          Leer la historia <ArrowUpRight size={16} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              </AnimatePresence>

              {/* Pista de swipe (solo móvil, primera vez) */}
              <AnimatePresence>
                {showHint && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="lg:hidden absolute top-4 right-4 z-20 pointer-events-none"
                    data-testid="hero-swipe-hint"
                  >
                    <span className="inline-flex items-center gap-2 rounded-full bg-black/60 backdrop-blur-md text-white text-xs font-semibold px-3.5 py-2 border border-white/20">
                      Desliza
                      <motion.span
                        animate={{ x: [0, 6, 0] }}
                        transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
                        className="inline-flex text-[#B4D844]"
                      >
                        <ChevronsRight size={16} />
                      </motion.span>
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* NEXT PEEK CARD */}
            <button
              type="button"
              onClick={() => go((index + 1) % n)}
              onMouseEnter={() => prefetchArticle(next.slug)}
              className="hidden lg:flex lg:col-span-4 xl:col-span-3 flex-col justify-between text-left p-5 rounded-2xl bg-[#161616] border border-white/10 hover:border-[#B4D844]/60 transition-colors group/peek"
              data-testid="hero-peek-next"
              aria-label={`Siguiente: ${next.title}`}
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white drop-shadow">A continuación</span>
              <div className="my-4 rounded-xl overflow-hidden ring-1 ring-white/20">
                <CoverImage src={next.cover_image} alt="" ratio="16/10" className="w-full h-40 object-cover group-hover/peek:scale-105 transition-transform duration-500" />
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">{next.format || "Noticia"}</span>
                <h3 className="display text-lg leading-snug mt-1.5 line-clamp-3 text-white">{next.title}</h3>
              </div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-white group-hover/peek:gap-2.5 transition-all">
                Ver siguiente <ArrowRight size={14} />
              </span>
            </button>
          </div>

          {/* CONTROLS: single progress bar + arrows (sin revelar el nº de historias) */}
          <div className="relative mt-6 pt-5 border-t border-white/10 flex items-center gap-4">
            <div className="relative h-[3px] flex-1 rounded-full bg-white/15 overflow-hidden" data-testid="hero-progress">
              <span
                key={`bar-${index}-${paused}`}
                className="absolute inset-y-0 left-0 bg-[#B4D844]"
                style={{ animation: paused ? "none" : `heroBar ${AUTOPLAY_MS}ms linear forwards` }}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" onClick={() => go(index - 1)} className="p-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-[#2A00D9] hover:border-[#2A00D9] transition-colors" data-testid="hero-carousel-prev-btn" aria-label="Anterior">
                <ArrowLeft size={16} />
              </button>
              <button type="button" onClick={() => go(index + 1)} className="p-2.5 rounded-full bg-white/5 border border-white/10 hover:bg-[#2A00D9] hover:border-[#2A00D9] transition-colors" data-testid="hero-carousel-next-btn" aria-label="Siguiente">
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-7 text-center">
          <Link to="/noticias" className="btn-cobalt" data-testid="hero-all-news-link">Ver todas las noticias <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  );
}
